import React, { FunctionComponent, useState } from "react";
import {
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import { colors } from "@bg/shared/lib/theme";
import { Cancel } from "@material-ui/icons";
import {
  EligibilityResponse,
  EstimateResponse,
} from "@bg/shared/lib/dal/models";
import Loading from "@bg/shared/lib/components/Loading";
import { gridContainerSpacing } from "@bg/shared/lib/theme/themes";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import _ from "lodash";

const useStyles = makeStyles({
  buttonContainer: {
    paddingTop: ".75em",
    paddingBottom: ".75em",
    fontWeight: 500,
    fontSize: "16px",
    fontFamily: "Roboto",
    color: colors.lightGrey,
  },
  floatRight: {
    float: "right",
  },
  redColor: {
    color: colors.candyRed,
  },
  closeDialogButton: {
    backgroundColor: colors.white,
    color: colors.powderBlue,
    borderColor: colors.powderBlue,
    textTransform: "none",
    "&:hover": {
      background: darken(colors.white, 0.1),
    },
    maxHeight: "36px",
    display: "flex",
    flexDirection: "column",
    minWidth: "136px",
  },
  getCostButton: {
    display: "flex",
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.powderBlue,
    "&:hover": {
      background: darken(colors.powderBlue, 0.1),
    },
    maxHeight: "36px",
    flexDirection: "column",
    minWidth: "136px",
  },
  estimateTitleText: {
    fontWeight: 400,
    fontSize: "16px",
    fontFamily: "Roboto",
    color: colors.mediumGray,
  },
  estimateInfoText: {
    fontWeight: 500,
    fontSize: "16px",
    fontFamily: "Roboto",
    color: colors.lightGrey,
  },
  dialogActions: {
    padding: "8px 24px 8px 24px",
  },
  informationArea: {
    paddingLeft: "24px",
  },
  tooltip: {
    fontSize: 16,
    verticalAlign: "text-top",
  },
});

interface EligibilityDialogProps {
  openDialog: boolean;
  closeDialog: () => void;
  showAsEligibility: boolean;
  eligibilityResponse: EligibilityResponse;
  costEstimateResponse: EstimateResponse;
  handleRequestCostEstimate: () => void;
  handleEstimationPDF: () => void;
}

const EligibilityDialog: FunctionComponent<EligibilityDialogProps> = ({
  openDialog,
  closeDialog,
  showAsEligibility = false,
  eligibilityResponse,
  costEstimateResponse,
  handleRequestCostEstimate,
  handleEstimationPDF,
}) => {
  const classes = useStyles();
  const [showLoading, setShowLoading] = useState(false);
  const handleFindOutCost = () => {
    setShowLoading(true);
    handleRequestCostEstimate();
    setShowLoading(false);
  };
  const activeEligibility =
    eligibilityResponse &&
    _.isEqual(eligibilityResponse.eligibilityStatus, "Eligible");
  if (!eligibilityResponse && !costEstimateResponse) {
    return <div></div>;
  }
  return (
    <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="lg">
      <DialogContent className={classes.buttonContainer}>
        <Button className={classes.floatRight} onClick={closeDialog}>
          <Cancel className={classes.redColor} fontSize="medium" />
        </Button>
        {showAsEligibility ? (
          <DialogTitle id="form-dialog-title">VOB Results</DialogTitle>
        ) : (
          <DialogTitle id="form-dialog-title">
            Estimated Out Of Pocket Cost
          </DialogTitle>
        )}
        <div className={classes.informationArea}>
          {showAsEligibility ? (
            activeEligibility ? (
              <DialogContentText>
                Great news, your insurance covers this test, click on the button
                to see how much out of pocket will cost.
              </DialogContentText>
            ) : (
              <DialogContentText>
                Unfortunately, your insurance does not cover this test.
              </DialogContentText>
            )
          ) : (
            <Grid container spacing={gridContainerSpacing}>
              <Grid item xs={3}>
                <div className={classes.estimateTitleText}>In Network</div>
                <div className={classes.estimateInfoText}>
                  {costEstimateResponse.isOutOfNetwork ? "No" : "Yes"}
                </div>
              </Grid>
              <Grid item xs={3}>
                <>
                  <div className={classes.estimateTitleText}>
                    Amount Payable{" "}
                    <Tooltip title="Amount payable by patient">
                      <HelpOutlineIcon className={classes.tooltip} />
                    </Tooltip>
                  </div>
                  {costEstimateResponse.amountPayable && (
                    <div className={classes.estimateInfoText}>
                      ${costEstimateResponse.amountPayable}
                    </div>
                  )}
                </>
              </Grid>
              <Grid item xs={3}>
                <div className={classes.estimateTitleText}>
                  Authorization required
                </div>
                <div className={classes.estimateInfoText}>
                  {costEstimateResponse.authorizationRequired ? "Yes" : "No"}
                </div>
              </Grid>
            </Grid>
          )}
        </div>
        {showLoading && <Loading />}
        <DialogActions className={classes.dialogActions}>
          {activeEligibility &&
            (showAsEligibility ? (
              <Button
                onClick={handleFindOutCost}
                color="primary"
                variant="contained"
                className={classes.getCostButton}
                disabled={showLoading}
              >
                Find out my cost
              </Button>
            ) : (
              <Button
                color="primary"
                variant="contained"
                className={classes.getCostButton}
                style={{ display: "hidden" }}
                disabled={showLoading}
                onClick={handleEstimationPDF}
              >
                Download PDF
              </Button>
            ))}
          <Button
            onClick={closeDialog}
            color="primary"
            variant="outlined"
            className={classes.closeDialogButton}
          >
            Close
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default EligibilityDialog;
